/**
 * Learner Agent N8N — Frontend Application
 */

import { createClient } from '@supabase/supabase-js';
import AuthService from '../../shared/auth-service.js';
import N8NClient from '../../shared/n8n-client.js';
import { t, getLang, setLanguage, applyTranslations } from './i18n.js';

// ===== Configuration =====
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://gynyxlvnirzzuurrulvg.supabase.co';
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd5bnl4bHZuaXJ6enV1cnJ1bHZnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTk1NjE3MTksImV4cCI6MjA3NTEzNzcxOX0.7rSvieisrEQ6PzhRsPvTtdZMB0gDzSwqkbUzNr02cvI';
const N8N_BASE_URL = import.meta.env.VITE_N8N_BASE_URL || 'https://flows.smartfront.cl';

// N8N webhook path — matches the "path" field in the Webhook node
const WORKFLOW_ID = 'learner-agent';

// Make Supabase client available globally for AuthService
window.supabase = { createClient };

// ===== Global State =====
let auth = null;
let n8n  = null;
let currentSession = null;
let syllabi = [];                // cached syllabus list
let selectedSyllabus = null;     // currently previewed syllabus
let reviewSessionId  = null;     // session loaded in the review view
let cachedSessions   = [];       // last loaded sessions (for inline confirm restore)
let assessmentModalSessionId = null; // session shown in assessment modal

// ===== Spinner =====
function showSpinner() {
    document.getElementById('spinner-overlay').classList.remove('hidden');
}
function hideSpinner() {
    document.getElementById('spinner-overlay').classList.add('hidden');
}

/** Wraps n8n.execute with spinner + error boundary */
async function callN8N(workflowId, data) {
    showSpinner();
    try {
        return await n8n.execute(workflowId, data);
    } finally {
        hideSpinner();
    }
}

// ===== Initialization =====
async function init() {
    try {
        // Apply stored language before anything else
        applyTranslations();

        console.log('[App] Initializing...');

        auth = new AuthService(SUPABASE_URL, SUPABASE_KEY);
        await auth.initialize();

        n8n = new N8NClient(N8N_BASE_URL, auth);

        auth.onAuthChange((user) => {
            if (user) {
                console.log('[App] User signed in:', user.email);
                showMainView();
                loadSessions();
            } else {
                console.log('[App] User signed out');
                showLoginView();
            }
        });

        setupEventListeners();
        console.log('[App] Initialized successfully');
    } catch (error) {
        console.error('[App] Initialization error:', error);
        alert(t('error_init'));
    }
}

// ===== Event Listeners =====
function setupEventListeners() {
    // Language toggle
    document.querySelectorAll('.lang-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            setLanguage(btn.dataset.lang);
            // Sync the session language dropdown to match the new app language
            const sel = document.getElementById('language-select');
            if (sel) sel.value = getLang();
        });
    });

    // Login
    document.getElementById('login-btn').addEventListener('click', async () => {
        try {
            await auth.signIn();
        } catch (error) {
            console.error('[App] Login error:', error);
            alert(t('error_signin'));
        }
    });

    // Logout
    document.getElementById('logout-btn').addEventListener('click', async () => {
        try {
            await auth.signOut();
        } catch (error) {
            console.error('[App] Logout error:', error);
        }
    });

    // Create session
    document.getElementById('create-session-btn').addEventListener('click', () => {
        showCreateSessionView();
    });

    document.getElementById('cancel-create-btn').addEventListener('click', () => {
        showSessionsListView();
    });

    document.getElementById('create-session-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        await createSession();
    });

    // Session type toggle (free / curricular)
    document.querySelectorAll('input[name="session_type"]').forEach(radio => {
        radio.addEventListener('change', onSessionTypeChange);
    });

    // Syllabus selector
    document.getElementById('syllabus-select').addEventListener('change', onSyllabusChange);

    // Preview objectives button
    document.getElementById('preview-objectives-btn').addEventListener('click', () => {
        if (selectedSyllabus) showObjectivesModal(selectedSyllabus);
    });

    // Close objectives modal
    document.getElementById('close-modal-btn').addEventListener('click', closeObjectivesModal);
    document.getElementById('objectives-modal').addEventListener('click', (e) => {
        if (e.target === e.currentTarget) closeObjectivesModal();
    });

    // Teaching
    document.getElementById('send-message-btn').addEventListener('click', () => {
        sendMessage();
    });

    document.getElementById('message-input').addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    });

    document.getElementById('end-session-btn').addEventListener('click', () => {
        endSession();
    });

    // Assessment result modal
    const hideAndReload = () => { hideAssessmentModal(); showSessionsListView(); loadSessions(); };
    document.getElementById('close-assessment-modal-btn').addEventListener('click', hideAndReload);
    document.getElementById('assessment-modal-back-btn').addEventListener('click', hideAndReload);
    document.getElementById('assessment-modal').addEventListener('click', (e) => {
        if (e.target === e.currentTarget) hideAndReload();
    });
    document.getElementById('assessment-modal-detail-btn').addEventListener('click', () => {
        const sid = assessmentModalSessionId;
        hideAssessmentModal();
        if (sid) viewSession(sid);
    });

    // Session review
    document.getElementById('back-to-sessions-btn').addEventListener('click', () => {
        reviewSessionId = null;
        document.getElementById('review-concepts-box').classList.add('hidden');
        showSessionsListView();
        loadSessions();
    });

    document.getElementById('review-continue-btn').addEventListener('click', () => {
        if (reviewSessionId) continueSession(reviewSessionId);
    });
}

// ===== Session Type UI =====
function onSessionTypeChange() {
    const type = getSelectedSessionType();
    const topicGroup    = document.getElementById('topic-group');
    const syllabusGroup = document.getElementById('syllabus-group');

    if (type === 'curricular') {
        topicGroup.classList.add('hidden');
        syllabusGroup.classList.remove('hidden');
        loadSyllabi(); // lazy load when first switching to curricular
    } else {
        topicGroup.classList.remove('hidden');
        syllabusGroup.classList.add('hidden');
    }
}

function getSelectedSessionType() {
    const checked = document.querySelector('input[name="session_type"]:checked');
    return checked ? checked.value : 'free';
}

function onSyllabusChange() {
    const sel = document.getElementById('syllabus-select');
    const syllabusId = sel.value;
    selectedSyllabus = syllabi.find(s => s.id === syllabusId) || null;
    document.getElementById('preview-objectives-btn').disabled = !selectedSyllabus;
}

// ===== Syllabi =====
async function loadSyllabi() {
    // Only load once
    if (syllabi.length > 0) return;

    try {
        console.log('[App] Loading syllabi...');
        const { data, error } = await auth.from('syllabi')
            .select('*')
            .eq('is_active', true)
            .order('title');

        if (error) throw error;

        syllabi = data || [];
        populateSyllabusDropdown();
    } catch (error) {
        console.error('[App] Error loading syllabi:', error);
        alert(t('error_load_syllabi'));
    }
}

function populateSyllabusDropdown() {
    const sel = document.getElementById('syllabus-select');
    // Keep the placeholder option, replace the rest
    sel.innerHTML = `<option value="">${t('syllabus_placeholder')}</option>`;
    syllabi.forEach(s => {
        const opt = document.createElement('option');
        opt.value = s.id;
        opt.textContent = `${s.title} (${s.grade_level || s.subject || ''})`;
        sel.appendChild(opt);
    });
}

// ===== Objectives Modal =====
function showObjectivesModal(syllabus) {
    document.getElementById('modal-syllabus-title').textContent = syllabus.title;

    // Big ideas
    const bigIdeasSection = document.getElementById('modal-big-ideas');
    const bigIdeasList    = document.getElementById('modal-big-ideas-list');
    let bigIdeas = syllabus.big_ideas || [];
    if (typeof bigIdeas === 'string') {
        try { bigIdeas = JSON.parse(bigIdeas); } catch(e) { bigIdeas = []; }
    }
    if (bigIdeas.length > 0) {
        bigIdeasList.innerHTML = bigIdeas.map(idea => `<li>${idea}</li>`).join('');
        bigIdeasSection.classList.remove('hidden');
    } else {
        bigIdeasSection.classList.add('hidden');
    }

    // Objectives — load from DB for this syllabus
    const objList = document.getElementById('modal-objectives-list');
    objList.innerHTML = `<p class="loading">${t('loading_sessions')}</p>`;

    auth.from('learning_objectives')
        .select('*')
        .eq('syllabus_id', syllabus.id)
        .order('order_num')
        .then(({ data, error }) => {
            if (error || !data) {
                objList.innerHTML = `<p class="error">${t('error_load_syllabi')}</p>`;
                return;
            }
            const catLabels = {
                core:       t('cat_core'),
                supporting: t('cat_supporting'),
                detail:     t('cat_detail')
            };
            objList.innerHTML = data.map(obj => `
                <div class="obj-item obj-cat-${obj.category}">
                    <span class="obj-badge obj-badge-${obj.category}">${catLabels[obj.category] || obj.category}</span>
                    <span class="obj-desc">${obj.description}</span>
                </div>
            `).join('');
        });

    document.getElementById('objectives-modal').classList.remove('hidden');
}

function closeObjectivesModal() {
    document.getElementById('objectives-modal').classList.add('hidden');
}

// ===== View Management =====
function showView(viewId) {
    document.querySelectorAll('.view').forEach(el => el.classList.add('hidden'));
    document.getElementById(viewId).classList.remove('hidden');
}

function showContentView(viewId) {
    document.querySelectorAll('.content-view').forEach(el => el.classList.add('hidden'));
    document.getElementById(viewId).classList.remove('hidden');
}

function showLoginView() {
    showView('login-view');
}

function showMainView() {
    showView('main-view');
    showSessionsListView();
    const user = auth.getUser();
    document.getElementById('user-email').textContent = user.email;
}

function showSessionsListView() {
    showContentView('sessions-list-view');
}

function showCreateSessionView() {
    showContentView('create-session-view');
    // Reset to free mode
    document.querySelector('input[name="session_type"][value="free"]').checked = true;
    document.getElementById('topic-group').classList.remove('hidden');
    document.getElementById('syllabus-group').classList.add('hidden');
    document.getElementById('syllabus-select').value = '';
    document.getElementById('preview-objectives-btn').disabled = true;
    selectedSyllabus = null;
    // Default session language to current UI language
    document.getElementById('language-select').value = getLang();
    document.getElementById('topic-input').focus();
}

function showTeachingView() {
    showContentView('teaching-view');
    document.getElementById('message-input').focus();
}

// ===== Session Management =====
async function loadSessions() {
    try {
        console.log('[App] Loading sessions...');

        const { data, error } = await auth.from('learner_sessions')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(20);

        if (error) throw error;
        renderSessions(data);
    } catch (error) {
        console.error('[App] Error loading sessions:', error);
        document.getElementById('sessions-container').innerHTML =
            `<p class="error">${t('error_load_sessions')}</p>`;
    }
}

function renderSessions(sessions) {
    const container = document.getElementById('sessions-container');
    cachedSessions = sessions || [];  // cache for inline confirm restore

    if (!sessions || sessions.length === 0) {
        container.innerHTML = `
            <div class="empty-state">
                <p>${t('empty_sessions')}</p>
            </div>`;
        document.getElementById('token-summary').classList.add('hidden');
        return;
    }

    renderTokenSummary(sessions);

    container.innerHTML = sessions.map(session => {
        const date     = new Date(session.created_at).toLocaleDateString(getLang() === 'es' ? 'es-CL' : 'en-US');
        const turns    = session.total_turns || 0;
        const concepts = session.concepts_extracted || 0;
        const isDone   = !!session.completed_at;
        const isCurricular = session.session_type === 'curricular';

        const typeBadge = isCurricular
            ? `<span class="session-badge badge-curricular">${t('badge_curricular')}</span>`
            : `<span class="session-badge badge-free">${t('badge_free')}</span>`;

        // Middle button communicates state: "Ver" (has evaluation) vs "Evaluar" (no evaluation yet)
        const midBtn = isDone
            ? `<button class="btn-secondary btn-sm" onclick="viewSession('${session.id}')">${t('view_learning')}</button>`
            : `<button class="btn-secondary btn-sm" onclick="evaluateSession('${session.id}')">${t('evaluate_btn')}</button>`;

        return `
        <div class="session-card" data-session-id="${session.id}">
            <div class="session-card-header">
                <h3>${session.topic_name}</h3>
                ${typeBadge}
            </div>
            <div class="session-meta">
                <span>${turns} ${t('turns_unit')}</span>
                <span>${concepts} ${t('concepts_unit')}</span>
                <span>${date}</span>
            </div>
            <div class="session-actions">
                <button class="btn-primary btn-sm" onclick="continueSession('${session.id}')">${t('continue_teaching')}</button>
                ${midBtn}
                <button class="btn-danger btn-sm" onclick="requestDeleteSession('${session.id}')">${t('delete_btn')}</button>
            </div>
        </div>`;
    }).join('');
}

function renderTokenSummary(sessions) {
    const box    = document.getElementById('token-summary');
    const totIn  = sessions.reduce((sum, s) => sum + (s.tokens_input  || 0), 0);
    const totOut = sessions.reduce((sum, s) => sum + (s.tokens_output || 0), 0);

    if (!totIn && !totOut) {
        box.classList.add('hidden');
        return;
    }

    const count = sessions.filter(s => (s.tokens_input || 0) + (s.tokens_output || 0) > 0).length;
    document.getElementById('token-sum-in').textContent =
        `${t('token_total_in')}: ${totIn.toLocaleString()}`;
    document.getElementById('token-sum-out').textContent =
        `${t('token_total_out')}: ${totOut.toLocaleString()}`;
    document.getElementById('token-sum-sessions').textContent =
        t('token_sessions_count').replace('{n}', count);

    box.classList.remove('hidden');
}

/** Step 1: show inline confirmation inside the card (no browser dialog) */
window.requestDeleteSession = function(sessionId) {
    const card = document.querySelector(`[data-session-id="${sessionId}"]`);
    if (!card) return;
    card.classList.add('card-confirming');
    const actions = card.querySelector('.session-actions');
    actions.innerHTML = `
        <div class="inline-confirm">
            <span class="inline-confirm-text">${t('confirm_delete_question')}</span>
            <button class="btn-danger btn-sm" onclick="deleteSession('${sessionId}')">
                ${t('confirm_delete_yes')}
            </button>
            <button class="btn-secondary btn-sm" onclick="cancelDeleteSession('${sessionId}')">
                ${t('confirm_delete_cancel')}
            </button>
        </div>`;
};

/** Step 2: user confirmed — proceed with deletion */
window.deleteSession = async function(sessionId) {
    try {
        // .select('id') lets us detect when RLS silently blocks the delete
        const { data: deleted, error } = await auth.from('learner_sessions')
            .delete()
            .eq('id', sessionId)
            .select('id');

        if (error) throw error;

        if (!deleted || deleted.length === 0) {
            console.warn('[App] Delete returned 0 rows for session:', sessionId,
                '— check that RLS policy "Users can delete own sessions" is active in Supabase.');
            throw new Error('Permission denied: session could not be deleted.');
        }

        console.log('[App] Session deleted:', sessionId);
        loadSessions();
    } catch (error) {
        console.error('[App] Error deleting session:', error);
        alert(t('error_delete_session'));
    }
};

/** Cancel: restore card to original state without a network call */
window.cancelDeleteSession = function(sessionId) {
    renderSessions(cachedSessions);
};

window.evaluateSession = async function(sessionId) {
    try {
        const response = await callN8N(WORKFLOW_ID, {
            action:     'end',
            session_id: sessionId
        });
        // Find topic name from cached sessions
        const session = cachedSessions.find(s => s.id === sessionId);
        const topic   = session?.topic_name || '';
        showAssessmentModal(sessionId, topic, response);
        // loadSessions() is called when user closes the modal
    } catch (error) {
        console.error('[App] Error evaluating session:', error);
        alert(t('error_end_session'));
    }
};

async function createSession() {
    try {
        const sessionType = getSelectedSessionType();
        const language    = document.getElementById('language-select').value;

        let topic      = '';
        let syllabusId = '';

        if (sessionType === 'curricular') {
            syllabusId = document.getElementById('syllabus-select').value;
            if (!syllabusId) {
                alert(t('error_syllabus_required'));
                return;
            }
            const syl = syllabi.find(s => s.id === syllabusId);
            topic = syl ? syl.title : syllabusId;
        } else {
            topic = document.getElementById('topic-input').value.trim();
            if (!topic) {
                alert(t('error_topic_required'));
                return;
            }
        }

        console.log('[App] Creating session via N8N:', topic, sessionType);

        const response = await callN8N(WORKFLOW_ID, {
            action:       'start',
            topic,
            language,
            session_type: sessionType,
            syllabus_id:  syllabusId || null
        });

        console.log('[App] N8N Response:', response);

        currentSession = {
            id:                       response.session_id,
            topic_name:               topic,
            language,
            session_type:             sessionType,
            syllabus_id:              syllabusId || null,
            total_turns:              0,
            concepts_extracted:       0,
            relationships_extracted:  0,
            tokens_input:             0,
            tokens_output:            0,
            conversation_history_json: []
        };

        console.log('[App] Session created:', currentSession.id);

        showTeachingView();
        document.getElementById('teaching-topic').textContent = topic;
        updateStats();
        renderMessages([]); // clear any previous session's messages
        addMessage('agent', response.message);
    } catch (error) {
        console.error('[App] Error creating session:', error);
        alert(t('error_create_session'));
    }
}

window.viewSession = async function(sessionId) {
    try {
        console.log('[App] Viewing session:', sessionId);

        const { data, error } = await auth.from('learner_sessions')
            .select('*')
            .eq('id', sessionId)
            .single();

        if (error) throw error;

        // Store for "Continue Teaching" button
        reviewSessionId = sessionId;

        // Header stats
        document.getElementById('review-topic').textContent    = data.topic_name;
        document.getElementById('review-turns').textContent    = data.total_turns || 0;
        document.getElementById('review-concepts').textContent = data.concepts_extracted || 0;
        document.getElementById('review-duration').textContent =
            data.duration_minutes != null ? Math.round(data.duration_minutes) : t('tokens_na');

        // Token summary
        const tokIn  = data.tokens_input  || 0;
        const tokOut = data.tokens_output || 0;
        document.getElementById('review-tokens').textContent =
            (tokIn || tokOut)
                ? `${tokIn.toLocaleString()} ${t('tokens_in')} / ${tokOut.toLocaleString()} ${t('tokens_out')}`
                : t('tokens_na');

        // Parse evaluation JSON
        let evalJson = data.objective_evaluation_json;
        if (typeof evalJson === 'string') {
            try { evalJson = JSON.parse(evalJson); } catch(e) { evalJson = null; }
        }

        const isCurricular = data.session_type === 'curricular' && evalJson?.type === 'curricular';

        // Assessment box (free mode) / Coverage box (curricular mode)
        const assessmentBox = document.getElementById('review-assessment');
        const coverageBox   = document.getElementById('review-coverage');

        if (isCurricular && evalJson?.coverage) {
            assessmentBox.classList.add('hidden');
            renderCoveragePanel(evalJson);
            coverageBox.classList.remove('hidden');
        } else {
            coverageBox.classList.add('hidden');
            const assessmentText = evalJson?.text || (typeof evalJson === 'string' ? evalJson : null);
            if (assessmentText) {
                document.getElementById('review-assessment-text').textContent = assessmentText;
                assessmentBox.classList.remove('hidden');
            } else {
                assessmentBox.classList.add('hidden');
            }
        }

        // Concepts panel
        renderConceptsPanel(data.knowledge_graph_json);

        // Conversation history
        let history = data.conversation_history_json || [];
        if (typeof history === 'string') {
            try { history = JSON.parse(history); } catch(e) { history = []; }
        }
        const msgContainer = document.getElementById('review-messages');
        msgContainer.innerHTML = '';
        history.forEach(msg => {
            const el = document.createElement('div');
            el.className = `message message-${msg.role}`;
            el.innerHTML = `<div class="message-content">${msg.content}</div>`;
            msgContainer.appendChild(el);
        });

        showContentView('session-review-view');
    } catch (error) {
        console.error('[App] Error loading session:', error);
        alert(t('error_load_session'));
    }
};

function renderCoveragePanel(evalJson) {
    const coverage = evalJson.coverage || {};
    const overall  = coverage.overall_pct || 0;
    const core     = coverage.core_pct    || 0;
    const achieved = coverage.achieved    || 0;
    const partial  = coverage.partial     || 0;
    const notCov   = coverage.not_covered || 0;

    // Progress bars
    document.getElementById('coverage-bar-overall').style.width = `${overall}%`;
    document.getElementById('coverage-pct-overall').textContent = `${overall}%`;
    document.getElementById('coverage-bar-core').style.width    = `${core}%`;
    document.getElementById('coverage-pct-core').textContent    = `${core}%`;

    // Detail line — replace template tokens
    const detail = t('coverage_detail')
        .replace('{achieved}',   achieved)
        .replace('{partial}',    partial)
        .replace('{not_covered}', notCov);
    document.getElementById('coverage-detail-text').textContent = detail;

    // Assessment summary text
    document.getElementById('coverage-assessment-text').textContent = evalJson.text || '';

    // Per-objective list
    const objectives = evalJson.objectives || [];
    const badgeLabels = {
        achieved:    t('obj_achieved'),
        partial:     t('obj_partial'),
        not_covered: t('obj_not_covered')
    };
    const objList = document.getElementById('coverage-objectives-list');
    objList.innerHTML = objectives.map(obj => `
        <div class="cov-obj cov-obj-${obj.coverage}">
            <span class="cov-badge cov-badge-${obj.coverage}">${badgeLabels[obj.coverage] || obj.coverage}</span>
            <div class="cov-obj-info">
                <span class="cov-obj-desc">${obj.description}</span>
                ${obj.evidence ? `<span class="cov-obj-evidence">${obj.evidence}</span>` : ''}
            </div>
        </div>
    `).join('');
}

function renderConceptsPanel(knowledgeGraph) {
    const box  = document.getElementById('review-concepts-box');
    const list = document.getElementById('review-concepts-list');

    let graph = knowledgeGraph;
    if (typeof graph === 'string') {
        try { graph = JSON.parse(graph); } catch(e) { graph = null; }
    }

    const concepts = graph?.concepts ? Object.values(graph.concepts) : [];

    if (concepts.length === 0) {
        list.innerHTML = `<p class="loading">${t('no_concepts')}</p>`;
        box.classList.remove('hidden');
        return;
    }

    // Sort: core → supporting → detail
    const order = { core: 0, supporting: 1, detail: 2 };
    concepts.sort((a, b) => (order[a.category] ?? 3) - (order[b.category] ?? 3));

    const catLabels = {
        core:       t('cat_core'),
        supporting: t('cat_supporting'),
        detail:     t('cat_detail')
    };

    list.innerHTML = concepts.map(c => `
        <div class="concept-card">
            <div class="concept-header">
                <span class="concept-name">${c.name}</span>
                ${c.category
                    ? `<span class="concept-badge concept-badge-${c.category}">${catLabels[c.category] || c.category}</span>`
                    : ''}
            </div>
            ${c.definition ? `<p class="concept-definition">${c.definition}</p>` : ''}
        </div>
    `).join('');

    box.classList.remove('hidden');
}

// ===== Assessment Result Modal =====
function showAssessmentModal(sessionId, topic, response) {
    assessmentModalSessionId = sessionId;

    document.getElementById('assessment-modal-topic').textContent = topic;

    // Assessment text
    document.getElementById('assessment-modal-text').textContent = response.assessment || '';

    // Coverage bars (curricular only)
    const coverageSection = document.getElementById('assessment-modal-coverage');
    if (response.coverage) {
        const cov = response.coverage;
        coverageSection.innerHTML = `
            <div class="assess-coverage">
                <div class="assess-cov-row">
                    <span>${t('coverage_overall')}</span>
                    <div class="coverage-bar-wrap">
                        <div class="coverage-bar" style="width:${cov.overall_pct}%"></div>
                    </div>
                    <span class="coverage-pct">${cov.overall_pct}%</span>
                </div>
                <div class="assess-cov-row">
                    <span>${t('coverage_core')}</span>
                    <div class="coverage-bar-wrap">
                        <div class="coverage-bar coverage-bar-core" style="width:${cov.core_pct}%"></div>
                    </div>
                    <span class="coverage-pct">${cov.core_pct}%</span>
                </div>
            </div>`;
        coverageSection.classList.remove('hidden');
    } else {
        coverageSection.classList.add('hidden');
    }

    // Stats chips
    const stats = response.stats || {};
    const chips = [
        { label: t('stats_turns'),    value: stats.total_turns    ?? '—' },
        { label: t('stats_concepts'), value: stats.concepts_learned ?? '—' },
        { label: t('stats_duration'), value: stats.duration_minutes != null
            ? `${stats.duration_minutes} ${t('stats_min')}` : '—' }
    ];
    document.getElementById('assessment-modal-stats').innerHTML = chips.map(c => `
        <div class="assess-stat-chip">
            <span class="assess-stat-label">${c.label}</span>
            <span class="assess-stat-value">${c.value}</span>
        </div>`).join('');

    document.getElementById('assessment-modal').classList.remove('hidden');
}

function hideAssessmentModal() {
    document.getElementById('assessment-modal').classList.add('hidden');
    assessmentModalSessionId = null;
}

window.continueSession = async function(sessionId) {
    try {
        console.log('[App] Continuing session:', sessionId);

        const { data, error } = await auth.from('learner_sessions')
            .select('*')
            .eq('id', sessionId)
            .single();

        if (error) throw error;

        currentSession = data;
        startTeaching();
    } catch (error) {
        console.error('[App] Error continuing session:', error);
        alert(t('error_load_session'));
    }
};

function startTeaching() {
    showTeachingView();
    document.getElementById('teaching-topic').textContent = currentSession.topic_name;
    updateStats();
    const messages = currentSession.conversation_history_json || [];
    renderMessages(messages);
}

async function sendMessage() {
    const input   = document.getElementById('message-input');
    const message = input.value.trim();
    if (!message) return;

    addMessage('user', message);
    input.value = '';

    // Disable input while waiting
    const sendBtn = document.getElementById('send-message-btn');
    sendBtn.disabled = true;
    input.disabled   = true;

    currentSession.total_turns += 1;
    updateStats();
    showThinkingBubble(); // B: thinking dots instead of global spinner

    try {
        // Call n8n directly (skip global spinner — thinking bubble is enough)
        const response = await n8n.execute(WORKFLOW_ID, {
            action:     'message',
            session_id: currentSession.id,
            message
        });

        removeThinkingBubble();
        addMessage('agent', response.message);

        if (response.concepts_learned) {
            currentSession.concepts_extracted = response.concepts_learned.length;
        }
        if (response.turn_count !== undefined) {
            currentSession.total_turns = response.turn_count;
        }
        updateStats();
    } catch (error) {
        removeThinkingBubble();
        console.error('[App] Error sending message:', error);
        addMessage('agent', t('error_send_message'));
    } finally {
        sendBtn.disabled = false;
        input.disabled   = false;
        input.focus();
    }
}

function showThinkingBubble() {
    const container = document.getElementById('messages');
    const el = document.createElement('div');
    el.id = 'thinking-bubble';
    el.className = 'message message-agent';
    el.innerHTML = `<div class="message-content">
        <div class="thinking-dots" aria-label="Pensando...">
            <span></span><span></span><span></span>
        </div>
    </div>`;
    container.appendChild(el);
    container.scrollTop = container.scrollHeight;
}

function removeThinkingBubble() {
    const el = document.getElementById('thinking-bubble');
    if (el) el.remove();
}

function addMessage(role, content) {
    const container = document.getElementById('messages');
    const el = document.createElement('div');
    el.className = `message message-${role}`;
    el.innerHTML = `<div class="message-content">${content}</div>`;
    container.appendChild(el);
    container.scrollTop = container.scrollHeight;
}

function renderMessages(messages) {
    const container = document.getElementById('messages');
    container.innerHTML = '';
    messages.forEach(msg => addMessage(msg.role, msg.content));
}

function updateStats() {
    document.getElementById('stat-turns').textContent         = currentSession.total_turns;
    document.getElementById('stat-concepts').textContent      = currentSession.concepts_extracted;
    document.getElementById('stat-relationships').textContent = currentSession.relationships_extracted;

    // Token display (will be 0 until N8N returns token data)
    const tokIn  = currentSession.tokens_input  || 0;
    const tokOut = currentSession.tokens_output || 0;
    document.getElementById('stat-tokens').textContent =
        (tokIn || tokOut)
            ? `${tokIn.toLocaleString()} / ${tokOut.toLocaleString()}`
            : t('tokens_na');
}

async function endSession() {
    try {
        const response = await callN8N(WORKFLOW_ID, {
            action:     'end',
            session_id: currentSession.id
        });
        showAssessmentModal(currentSession.id, currentSession.topic_name, response);
        // Navigation (showSessionsListView + loadSessions) happens when user closes modal
    } catch (error) {
        console.error('[App] Error ending session:', error);
        alert(t('error_end_session'));
    }
}

// ===== Initialize App =====
init();
